import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { ApiService } from 'src/app/core/api/api.service';
import { ADMIN_ORDERS_ENDPOINTS } from 'src/app/core/api/endpoints/admin-orders.endpoints';
import { BUTTON_LOADING_ID } from 'src/app/shared/global/http-context-tokens';
import { AddressDataResponse } from '../../models/order-info-model';

@Injectable({
  providedIn: 'root',
})
export class AdminOrdersService {
  private api = inject(ApiService);
  private newOrder = new BehaviorSubject({});
  currentNewOrder = this.newOrder.asObservable();

  changeOrder(updateOrder: {}) {
    this.newOrder.next(updateOrder);
  }

  getAdminOrdersByPage(query): Observable<any> {
    return this.api
      .get(ADMIN_ORDERS_ENDPOINTS.V1.ORDERS_INDIVIDUAL, { params: query })
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  getFinanceOrdersByPage(query): Observable<any> {
    return this.api
      .get(ADMIN_ORDERS_ENDPOINTS.V1.FINANCE_ORDERS, { params: query })
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  getAdminOrder(id): Observable<any> {
    return this.api.get<any>(ADMIN_ORDERS_ENDPOINTS.V1.ADMIN_ORDER_DETAIL(id)).pipe(
      map((data: any) => {
        return data;
      }),
      catchError(this.errorHandler)
    );
  }

  updateOrder(id, order, buttonId?): Observable<any> {
    return this.api
      .put<any>(ADMIN_ORDERS_ENDPOINTS.V1.ADMIN_ORDER_DETAIL(id), order,
        {context: new HttpContext().set(BUTTON_LOADING_ID, buttonId)});
  }

  createNewOrder(order): Observable<any> {
    return this.api.post<any>(ADMIN_ORDERS_ENDPOINTS.V1.ADMIN_ORDERS, order).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  getAddressData(id: number, location: string): Observable<AddressDataResponse> {
    return this.api.put(ADMIN_ORDERS_ENDPOINTS.V1.ADMIN_ORDER_DETAIL(id), { location });
  }
 

  deleteAdminOrder(orderId: number): Observable<any> {
    return this.api
      .delete(ADMIN_ORDERS_ENDPOINTS.V1.ADMIN_ORDERS, { body: orderId })
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  getMessagesLog(orderId): Observable<any> {
    return this.api
      .get<any>(ADMIN_ORDERS_ENDPOINTS.V1.ORDER_MESSAGES_LOG(orderId))
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  deleteMessage(msgPK): Observable<any> {
    return this.api
      .delete<any>(ADMIN_ORDERS_ENDPOINTS.V1.DELETE_ORDER_MESSAGE(msgPK))
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  changePaymentStatus(orderID, isRecived): Observable<any> {
    return this.api
      .put<any>(ADMIN_ORDERS_ENDPOINTS.V1.ORDER_COST_IS_RECEIVED(orderID), isRecived)
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  changePaymentInspectorStatus(orderID: number, isPaid: boolean): Observable<any> {
    return this.api
      .put<any>(ADMIN_ORDERS_ENDPOINTS.V1.INSPECTOR_SHARE_IS_PAID_DETAIL(orderID), isPaid)
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  updateZapierData(id): Observable<any> {
    let body = {
      send_to_zapier: true,
    };
    return this.api
      .put<any>(ADMIN_ORDERS_ENDPOINTS.V1.ZAPIER_SEND_MANUALLY(id), body)
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  getVisitSummary(id): Observable<any> {
    return this.api
      .get(ADMIN_ORDERS_ENDPOINTS.V1.VISIT_SUMMARY(id))
      .pipe(shareReplay());
  }

  notifyInspectors(orderId) {
    return this.api.put(ADMIN_ORDERS_ENDPOINTS.V1.ORDER_BOOL(orderId), {});
  }

  applyCoupon(orderId): Observable<any> {
    return this.api.post(ADMIN_ORDERS_ENDPOINTS.V1.COUPON_APPLY, orderId);
  }

  linkOrders(linkedOrderId, data): Observable<any> {
    return this.api.post(ADMIN_ORDERS_ENDPOINTS.V1.LINK_ORDERS(linkedOrderId), data);
  }

  unLinkOrders(orders): Observable<any> {
    return this.api.post(ADMIN_ORDERS_ENDPOINTS.V1.UNLINK_ORDERS, orders);
  }

  changeOrdersStatus(ordersData, action): Observable<any> {
   let payload={
    "orders_data": ordersData,
    "action": action
    }

   return this.api.put(ADMIN_ORDERS_ENDPOINTS.V1.BULK_UPDATE_ORDER_STATUS, payload);
  }

  errorHandler(error: HttpErrorResponse) {
    return throwError(error.message || 'server error.');
  }

  duplicateOrder(data, buttonId?): Observable<any> {
    const orderIdData = {
      payload: data,
    }
    return this.api.post(ADMIN_ORDERS_ENDPOINTS.V1.DUPLICATE_ORDER, orderIdData,
      {context: new HttpContext().set(BUTTON_LOADING_ID, buttonId)}
    );
  }

  syncTbcStatus(orderId): Observable<any> {
    return this.api.post(ADMIN_ORDERS_ENDPOINTS.V1.SET_SYNC_STATUS_READY(orderId), {});
  }

  bulkReassignQC(data): Observable<any> {
    return this.api.put<any>(ADMIN_ORDERS_ENDPOINTS.V1.BULK_REASSIGN_QC, data);
  }

  updateQoudInfo(orderId: number): Observable<any> {
    return this.api.post<any>(ADMIN_ORDERS_ENDPOINTS.V1.SEND_ORDER_DATA_TO_QOYOD(orderId), {});
  }

  createQoyoodInvoice(orderId: number): Observable<any> {
    return this.api.post<any>(ADMIN_ORDERS_ENDPOINTS.V1.QOYOOD_INVOICES(orderId), {});
  }

  exportFinanceSheet(query): Observable<any> {
    return this.api.post<any>(ADMIN_ORDERS_ENDPOINTS.V1.FINANCE_EXPORT_EXCEL, null, {params: query});
  }

  getRefrenceNumbers(params: any): Observable<any> {
    return this.api.get<any>(ADMIN_ORDERS_ENDPOINTS.V1.MAIN_ORDERS, { params: params });
  }


  getClientGoals(): Observable<any> {
    return this.api.get(ADMIN_ORDERS_ENDPOINTS.V1.CLIENT_GOALS);
  }

  getPropertyRelations(): Observable<any> {
    return this.api.get(ADMIN_ORDERS_ENDPOINTS.v2.PROPERTY_RELATIONS);
  }
}
